import React, { useState, useMemo } from "react";
import {
  LayoutGrid, FileText, TrendingUp, Receipt, PhoneCall, Gauge, Scale, Banknote,
  Send, Sparkles, X, ShieldCheck, AlertTriangle, RefreshCw, Inbox, Zap,
  CheckCircle2, ArrowLeftRight, Clock,
} from "lucide-react";

/* Radha — Module 7: Cash Application & Reconciliation + Auto-Capture (preview)
   Seeded to match 007_reconciliation.sql. Auto-post threshold = 0.90 (zero-touch). */

const C = {
  primary: "#1B54F8", success: "#00A87A", warning: "#C8861A", danger: "#D63B2F",
  ai: "#6E44D4", aiBg: "#F5F3FF", bg: "#F3F2EE", surface: "#FFFFFF",
  border: "#E4E3DE", text: "#08090C", muted: "#7A7975",
};
const mono = "'DM Mono', ui-monospace, Menlo, monospace";
const sans = "'Inter', system-ui, sans-serif";
function formatINR(n) {
  if (n == null || isNaN(n)) return "—";
  const a = Math.abs(n), s = n < 0 ? "-" : "";
  if (a >= 1e7) return `${s}₹${(a / 1e7).toFixed(2)}Cr`;
  if (a >= 1e5) return `${s}₹${(a / 1e5).toFixed(1)}L`;
  return `${s}₹${a.toLocaleString("en-IN")}`;
}

/* 8 transactions from 007_reconciliation.sql */
const TXNS = [
  { ref: "HDFC230415123456", payer: "Reliance Industries Ltd", amount: 5900000, inv: "INV-2026-0301", sig: { u: 1, a: 1, p: 1, n: 1 }, score: 0.98, status: "posted", doc: "CLR-2026-0301", lat: 712 },
  { ref: "ICIC230415789012", payer: "Tata Consultancy Services", amount: 3776000, inv: "INV-2026-0302", sig: { u: 1, a: 1, p: 1, n: 1 }, score: 0.97, status: "posted", doc: "CLR-2026-0302", lat: 688 },
  { ref: "SBIN230415345678", payer: "Infosys Ltd", amount: 3304000, inv: "INV-2026-0303", sig: { u: 1, a: 1, p: 1, n: 0 }, score: 0.95, status: "posted", doc: "CLR-2026-0303", lat: 803 },
  { ref: "HDFC230419555111", payer: "Mahindra & Mahindra", amount: 1770000, inv: "INV-2026-0304", sig: { u: 1, a: 1, p: 1, n: 1 }, score: 0.93, status: "posted", doc: "CLR-2026-0304", lat: 745 },
  { ref: "ltfinance@icici", payer: "L&T Finance Holdings", amount: 4838000, inv: "INV-2026-0306", sig: { u: 0, a: 1, p: 1, n: 1 }, score: 0.91, status: "posted", doc: "CLR-2026-0306", lat: 690 },
  { ref: "HDFC230420777222", payer: "Bajaj Finance Ltd", amount: 1770000, inv: "INV-2026-0307", sig: { u: 1, a: 1, p: 1, n: 0 }, score: 0.90, status: "posted", doc: "CLR-2026-0307", lat: 770 },
  { ref: "ICIC230511888333", payer: "Wipro Ltd", amount: 600000, inv: "INV-2026-0305", sig: { u: 0, a: 0, p: 1, n: 1 }, score: 0.62, status: "exception", reason: "amount_mismatch" },
  { ref: "SBIN230515999444", payer: "Unknown Remitter", amount: 250000, inv: null, sig: { u: 0, a: 0, p: 0, n: 0 }, score: 0.30, status: "exception", reason: "unknown_payer" },
];

const MODULES = [
  { code: "M1", name: "Collections Copilot", icon: PhoneCall },
  { code: "M2", name: "Dispute Resolution", icon: Scale },
  { code: "M3", name: "Contract MIS", icon: FileText },
  { code: "M4", name: "Revenue Forecast", icon: TrendingUp },
  { code: "M5", name: "Invoicing Engine", icon: Receipt },
  { code: "M6", name: "Payment Reminders", icon: Send },
  { code: "M7", name: "Cash Application", icon: Banknote, active: true },
  { code: "M8", name: "Cash Flow Forecast", icon: Gauge },
  { code: "M9", name: "CRM Pipeline", icon: LayoutGrid },
];

function SignalChips({ sig }) {
  const defs = [["U", sig.u, "UTR"], ["A", sig.a, "Amount"], ["P", sig.p, "Payer"], ["N", sig.n, "Narration"]];
  return <span style={{ display: "inline-flex", gap: 3 }}>
    {defs.map(([l, on, title]) => (
      <span key={l} title={title} style={{ width: 16, height: 16, borderRadius: 4, display: "grid", placeItems: "center", fontFamily: mono, fontSize: 9, fontWeight: 500, background: on ? `${C.success}1a` : C.bg, color: on ? C.success : C.muted, border: `0.5px solid ${on ? C.success + "55" : C.border}` }}>{l}</span>
    ))}
  </span>;
}
function Badge({ children, color }) {
  return <span style={{ fontFamily: mono, fontSize: 9, letterSpacing: 0.5, textTransform: "uppercase", color, background: `${color}14`, border: `0.5px solid ${color}33`, borderRadius: 20, padding: "3px 8px", whiteSpace: "nowrap" }}>{children}</span>;
}
function KPICard({ label, value, sub, accent }) {
  return <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: "16px 18px", flex: 1, minWidth: 140 }}>
    <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>{label}</div>
    <div style={{ fontFamily: mono, fontSize: 24, fontWeight: 500, color: accent || C.text, marginTop: 8 }}>{value}</div>
    <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{sub}</div>
  </div>;
}

export default function Reconciliation() {
  const [view, setView] = useState("data");
  const [drawer, setDrawer] = useState(null);

  const stats = useMemo(() => {
    const posted = TXNS.filter(t => t.status === "posted");
    const lat = posted.map(t => t.lat);
    return {
      total: TXNS.length, posted: posted.length,
      exceptions: TXNS.filter(t => t.status === "exception").length,
      rate: Math.round((posted.length / TXNS.length) * 100),
      avgLat: Math.round(lat.reduce((s, x) => s + x, 0) / lat.length),
    };
  }, []);
  const exceptions = TXNS.filter(t => t.status === "exception");

  return (
    <div style={{ display: "flex", minHeight: 640, background: C.bg, fontFamily: sans, color: C.text }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Inter:wght@400;500;600&display=swap');
        .rrow:hover{background:${C.bg}!important}.nav:hover{background:#ffffff14}`}</style>

      <aside style={{ width: 224, background: "#0E1118", color: "#E7E9EE", padding: "20px 12px", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 8px 18px" }}>
          <div style={{ width: 26, height: 26, borderRadius: 8, background: C.ai, display: "grid", placeItems: "center", fontFamily: mono, fontWeight: 600 }}>र</div>
          <div style={{ fontWeight: 600 }}>Radha</div>
        </div>
        {MODULES.map(m => { const Icon = m.icon; return (
          <div key={m.code} className="nav" style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderRadius: 8, marginBottom: 2, cursor: "pointer", background: m.active ? C.primary : "transparent", color: m.active ? "#fff" : "#AEB4C0" }}>
            <Icon size={15} /><span style={{ fontSize: 12.5 }}>{m.name}</span>
            <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 8, opacity: 0.6 }}>{m.code}</span>
          </div>); })}
        <div style={{ marginTop: 18, padding: "0 8px", fontSize: 10, color: "#5A6172" }}>Bharat Industries Ltd</div>
      </aside>

      <main style={{ flex: 1, padding: "22px 26px", overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontFamily: mono, fontSize: 10, color: C.muted, letterSpacing: 0.6 }}>M7 · CASH APPLICATION & RECONCILIATION + AUTO-CAPTURE</div>
            <h1 style={{ fontSize: 22, fontWeight: 600, margin: "4px 0 0" }}>Reconciliation</h1>
          </div>
          <div style={{ display: "flex", gap: 4, background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 10, padding: 4 }}>
            {["data", "loading", "empty", "error"].map(s => (
              <button key={s} onClick={() => setView(s)} style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.5, border: "none", borderRadius: 7, padding: "5px 9px", cursor: "pointer", background: view === s ? C.text : "transparent", color: view === s ? "#fff" : C.muted }}>{s}</button>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, marginTop: 18, flexWrap: "wrap" }}>
          <KPICard label="Auto-Post Rate" value={`${stats.rate}%`} sub={`${stats.posted} of ${stats.total} zero-touch`} accent={C.success} />
          <KPICard label="Avg ERP Latency" value={`${stats.avgLat}ms`} sub="target < 900ms" accent={C.primary} />
          <KPICard label="Exceptions" value={stats.exceptions} sub="need manual review" accent={C.warning} />
          <KPICard label="Captured" value={stats.total} sub="bank credits today" />
        </div>

        <div style={{ display: "flex", gap: 16, marginTop: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
          <div style={{ flex: "1 1 560px", minWidth: 320 }}>
            {/* EF-CORE partial proposal */}
            <div style={{ background: C.aiBg, border: `0.5px solid ${C.ai}33`, borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Sparkles size={14} color={C.ai} />
                <span style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.8, color: C.ai }}>EF-CORE · RECONCILIATION</span>
                <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 9, color: C.ai }}>confidence 0.62</span>
              </div>
              <p style={{ margin: "10px 0 0", fontSize: 13.5, lineHeight: 1.5, color: "#2A2A2E" }}>
                A <b>₹6,00,000</b> credit from <b>Wipro Ltd</b> references a part payment, but the amount is well below
                <b> INV-2026-0305</b> (₹11.56L) — not a clean full match. Recommended: apply as a partial receipt and leave the balance open.
              </p>
              <button onClick={() => setDrawer(TXNS[6])} style={{ marginTop: 12, background: C.ai, color: "#fff", border: "none", borderRadius: 8, padding: "7px 13px", fontSize: 12, fontWeight: 500, cursor: "pointer" }}>Review & apply partial</button>
            </div>

            {/* Transaction table */}
            <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
              <div style={{ padding: "12px 14px", borderBottom: `0.5px solid ${C.border}`, fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Captured bank transactions</div>
              {view === "loading" && <Skeleton />}
              {view === "error" && <ErrorState />}
              {view === "empty" && <EmptyState />}
              {view === "data" && (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr>{["Payer / UTR", "Amount", "Signals", "Score", "Status"].map((h, i) => (
                    <th key={h} style={{ textAlign: i === 1 ? "right" : "left", fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted, fontWeight: 500, padding: "10px 14px", borderBottom: `0.5px solid ${C.border}` }}>{h}</th>
                  ))}</tr></thead>
                  <tbody>
                    {TXNS.map((t, i) => {
                      const exc = t.status === "exception";
                      return (
                        <tr key={i} className="rrow" onClick={() => exc && setDrawer(t)} style={{ cursor: exc ? "pointer" : "default", borderBottom: `0.5px solid ${C.border}` }}>
                          <td style={{ padding: "10px 14px" }}>
                            <div style={{ fontSize: 13, fontWeight: 500 }}>{t.payer}</div>
                            <div style={{ fontFamily: mono, fontSize: 10, color: C.muted, marginTop: 2 }}>{t.ref}{t.inv ? ` → ${t.inv}` : ""}</div>
                          </td>
                          <td style={{ padding: "10px 14px", textAlign: "right", fontFamily: mono, fontSize: 13 }}>{formatINR(t.amount)}</td>
                          <td style={{ padding: "10px 14px" }}><SignalChips sig={t.sig} /></td>
                          <td style={{ padding: "10px 14px", fontFamily: mono, fontSize: 12, color: t.score >= 0.9 ? C.success : C.warning }}>{t.score.toFixed(2)}</td>
                          <td style={{ padding: "10px 14px" }}>
                            {t.status === "posted"
                              ? <Badge color={C.success}>posted</Badge>
                              : <Badge color={C.danger}>{t.reason.replace("_", " ")}</Badge>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Exception queue + ERP log */}
          <div style={{ flex: "0 0 260px", minWidth: 220 }}>
            <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4 }}>
                <AlertTriangle size={13} color={C.warning} />
                <span style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Exception queue</span>
              </div>
              {exceptions.map((t, i) => (
                <div key={i} onClick={() => setDrawer(t)} style={{ marginTop: 12, paddingTop: i ? 12 : 0, borderTop: i ? `0.5px solid ${C.border}` : "none", cursor: "pointer" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 12.5, fontWeight: 500 }}>{t.payer}</span>
                    <span style={{ fontFamily: mono, fontSize: 12 }}>{formatINR(t.amount)}</span>
                  </div>
                  <div style={{ marginTop: 4 }}><Badge color={C.danger}>{t.reason.replace("_", " ")}</Badge></div>
                </div>
              ))}
            </div>
            <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: 16, marginTop: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4 }}>
                <Zap size={13} color={C.success} />
                <span style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>ERP posting log</span>
              </div>
              {TXNS.filter(t => t.status === "posted").slice(0, 4).map((t, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, paddingTop: i ? 10 : 0, borderTop: i ? `0.5px solid ${C.border}` : "none" }}>
                  <span style={{ fontFamily: mono, fontSize: 11 }}>{t.doc}</span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontFamily: mono, fontSize: 11, color: t.lat < 900 ? C.success : C.warning }}><Clock size={11} />{t.lat}ms</span>
                </div>
              ))}
            </div>
            <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: 16, marginTop: 16 }}>
              <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Controls</div>
              {[["Zero-touch ≥ 0.90", "auto-post threshold"], ["Latency SLA", "sub-900ms posting"], ["Append-only audit", "every match logged"]].map((r, i) => (
                <div key={i} style={{ display: "flex", gap: 9, alignItems: "flex-start", marginTop: 12 }}>
                  <ShieldCheck size={15} color={C.success} style={{ marginTop: 1, flexShrink: 0 }} />
                  <div><div style={{ fontSize: 12.5, fontWeight: 500 }}>{r[0]}</div><div style={{ fontSize: 11, color: C.muted }}>{r[1]}</div></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      {drawer && <MatchDrawer txn={drawer} onClose={() => setDrawer(null)} />}
    </div>
  );
}

function Skeleton() { return <div style={{ padding: 14 }}>{[...Array(5)].map((_, i) => <div key={i} style={{ height: 18, background: C.border, borderRadius: 6, marginBottom: 14, opacity: 0.6, animation: "pulse 1.4s ease-in-out infinite" }} />)}<style>{`@keyframes pulse{0%,100%{opacity:.35}50%{opacity:.7}}`}</style></div>; }
function ErrorState() { return <div style={{ padding: 40, textAlign: "center" }}><AlertTriangle size={26} color={C.danger} /><div style={{ marginTop: 10, fontWeight: 500 }}>Couldn’t load transactions</div><button style={{ marginTop: 14, display: "inline-flex", alignItems: "center", gap: 6, background: C.primary, color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, cursor: "pointer" }}><RefreshCw size={13} /> Retry</button></div>; }
function EmptyState() { return <div style={{ padding: 44, textAlign: "center" }}><Inbox size={26} color={C.muted} /><div style={{ marginTop: 10, fontWeight: 500 }}>No transactions captured</div><div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>Connect a bank feed (HDFC / ICICI / SBI) to auto-capture credits.</div></div>; }

function MatchDrawer({ txn, onClose }) {
  const [done, setDone] = useState(null);
  const isPartial = txn.reason === "amount_mismatch";
  const sig = [["UTR match", txn.sig.u], ["Amount match", txn.sig.a], ["Payer match", txn.sig.p], ["Narration match", txn.sig.n]];

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "#08090C55", display: "flex", justifyContent: "flex-end", zIndex: 50 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 400, background: C.surface, height: "100%", padding: 22, overflow: "auto", boxShadow: "-12px 0 40px #0003" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontFamily: mono, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Manual reconciliation</div>
          <X size={18} color={C.muted} style={{ cursor: "pointer" }} onClick={onClose} />
        </div>
        <h3 style={{ margin: "8px 0 2px", fontSize: 17 }}>{txn.payer}</h3>
        <div style={{ fontFamily: mono, fontSize: 13, color: C.muted }}>{txn.ref} · {formatINR(txn.amount)}</div>

        <div style={{ marginTop: 14, background: "#FBE9E7", border: `0.5px solid ${C.danger}33`, borderRadius: 8, padding: 11 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, fontWeight: 500, color: C.danger }}>
            <AlertTriangle size={14} /> {txn.reason.replace("_", " ")} · score {txn.score.toFixed(2)}
          </div>
          <div style={{ fontSize: 11.5, color: C.muted, marginTop: 5, lineHeight: 1.45 }}>
            {isPartial
              ? "Below the 0.90 auto-post threshold: payer and narration match INV-2026-0305, but the amount is short of the invoice total."
              : "Payer not recognised and the narration has no invoice reference."}
          </div>
        </div>

        <div style={{ marginTop: 16, fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Match signals</div>
        <div style={{ marginTop: 8 }}>
          {sig.map(([l, on], i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderTop: i ? `0.5px solid ${C.border}` : "none" }}>
              <span style={{ fontSize: 12.5 }}>{l}</span>
              {on ? <CheckCircle2 size={15} color={C.success} /> : <X size={15} color={C.muted} />}
            </div>
          ))}
        </div>

        {done ? (
          <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 8, background: "#E9F7F1", border: `0.5px solid ${C.success}33`, borderRadius: 9, padding: 12, fontSize: 13, color: C.success }}>
            <CheckCircle2 size={15} /> {done}. Logged to the audit trail.
          </div>
        ) : (
          <div style={{ marginTop: 18 }}>
            {isPartial && (
              <button onClick={() => setDone("Applied ₹6,00,000 as a partial against INV-2026-0305")} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: C.primary, color: "#fff", border: "none", borderRadius: 9, padding: "11px 0", fontSize: 13, fontWeight: 500, cursor: "pointer", marginBottom: 8 }}>
                <ArrowLeftRight size={14} /> Apply as partial receipt
              </button>
            )}
            <button onClick={() => setDone("Routed to AR manager for manual matching")} style={{ width: "100%", background: "transparent", color: C.muted, border: `0.5px solid ${C.border}`, borderRadius: 9, padding: "11px 0", fontSize: 13, cursor: "pointer" }}>
              Route to AR manager
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
